import { filterClasses, warn } from "@swivl-mono/helpers";
import React, { ChangeEvent, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import { BiEditAlt } from "react-icons/bi";
import { RiDeleteBinLine } from "react-icons/ri";
import Resizer from "react-image-file-resizer";

import { Spinner } from "../Spinner/Spinner";
import { AvatarCropModal } from "./AvatarCropModal";
import "./ImageUploader.scss";

export enum ImageUploaderType {
  AVATAR = "AVATAR",
  LOCATION = "LOCATION"
}

export const DEFAULT_AVATAR = "https://swivl-cdn.s3-us-west-1.amazonaws.com/studio/member-avatar-blank.png";

interface Props {
  isEditable?: boolean;
  type: ImageUploaderType;
  imageURL?: string;
  defaultImageURL?: string;
  imageUploaded?: (url: string) => void;
  onImageDeleted?: () => void;
  name?: string;
  size?: number;
  round?: boolean;
  text?: React.ReactNode;
  onUploaderStatusChanging?: (isUploading: boolean) => void;
}

const getImageFormat = (file: File): { format: string; quality: number; extension: string } => {
  if (file.type === "image/png") {
    return { format: "PNG", quality: 100, extension: "png" };
  }
  return { format: "JPEG", quality: 80, extension: "jpg" };
};

const resizeFile = (file: File, type: ImageUploaderType): Promise<File> => {
  let width = 300;
  let height = 300;
  if (type === ImageUploaderType.LOCATION) {
    width = 800;
    height = 800;
  }

  const { format, quality } = getImageFormat(file);

  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      width,
      height,
      format,
      quality,
      0,
      (uri) => {
        resolve(uri as File);
      },
      "file"
    );
  });
};

const uploadFile = async (file: File, type: ImageUploaderType, skipResize = false): Promise<string> => {
  const { extension } = getImageFormat(file);
  const fileToUpload = skipResize ? file : await resizeFile(file, type);

  const { uploadUrl, fileUrl } = await fetch(`${(import.meta as any).env.VITE_SERVER_URL}/api/s3/org/1/get-signed-url/file.${extension}`).then(
    (res) => res.json()
  );
  await fetch(uploadUrl, { method: "PUT", body: fileToUpload });

  return fileUrl;
};

function AvatarUploader({
  imageURL,
  defaultImageURL = DEFAULT_AVATAR,
  isEditable = true,
  imageUploaded,
  onImageDeleted,
  size = 64,
  round = false,
  name
}: Props): React.ReactNode {
  const displayURL = imageURL || defaultImageURL;
  const [isHovered, setIsHovered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setIsCropModalOpen(true);
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function handleCropComplete(croppedFile: File): Promise<void> {
    setIsCropModalOpen(false);
    setCropImageSrc(null);
    setIsUploading(true);
    try {
      const fileUrl = await uploadFile(croppedFile, ImageUploaderType.AVATAR, true);
      imageUploaded?.(fileUrl);
    } catch (error) {
      warn("Error uploading avatar", error);
    } finally {
      setIsUploading(false);
    }
  }

  function handleCropClose(): void {
    setIsCropModalOpen(false);
    setCropImageSrc(null);
  }

  return (
    <>
      <div
        className={filterClasses("relative shrink-0 overflow-hidden", round ? "rounded-full" : "rounded")}
        style={{ width: size, height: size }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img src={displayURL} alt="Avatar" className="size-full object-cover" />
        {isEditable && isHovered && !isUploading && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/22 backdrop-blur-[1.5px]">
            <button type="button" onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
              <BiEditAlt className="size-4 text-white hover:text-orange-500" />
            </button>
            {imageURL && onImageDeleted && (
              <button type="button" onClick={onImageDeleted} className="cursor-pointer">
                <RiDeleteBinLine className="size-4 text-white hover:text-orange-500" />
              </button>
            )}
          </div>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/22 backdrop-blur-[1.5px]">
            <Spinner />
          </div>
        )}
        <input name={name} ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png" onChange={handleFileChange} className="hidden" />
      </div>
      {cropImageSrc && (
        <AvatarCropModal
          imageSrc={cropImageSrc}
          isOpen={isCropModalOpen}
          onClose={handleCropClose}
          onCropComplete={handleCropComplete}
          round={round}
        />
      )}
    </>
  );
}

export const ImageUploader = (props: Props) => {
  if (props.type === ImageUploaderType.AVATAR) {
    return <AvatarUploader {...props} />;
  }

  const { imageURL, defaultImageURL } = props;
  const displayURL = imageURL || defaultImageURL;
  const [file, setFile] = React.useState<File>();
  const [uploading, setUploading] = React.useState(false);

  if (!props.isEditable) {
    return <div className="ImageUploader">{displayURL && <img src={displayURL} alt="Location" />}</div>;
  }

  return (
    <div className={`ImageUploader ${props.type}`}>
      {!displayURL && <div className="ImageUploaderEmptyLabel">Location photo not found</div>}
      <div className="ImageUploaderInner">
        {uploading ? (
          <Spinner />
        ) : (
          <Dropzone
            accept={{
              "image/png": [".png"],
              "image/jpeg": [".jpg", ".jpeg"]
            }}
            maxFiles={1}
            onDrop={async (acceptedFiles: File[]) => {
              setUploading(true);
              props.onUploaderStatusChanging?.(true);

              const file = acceptedFiles[0];
              const fileUrl = await uploadFile(file, props.type);
              setFile(file);

              props.imageUploaded?.(fileUrl);
              props.onUploaderStatusChanging?.(false);
              setUploading(false);
            }}
            onError={(error) => warn(error)}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div className="innerWrapper" {...getRootProps()}>
                <input {...getInputProps()} name={props.name} />
                {props.text || <p>Drag 'n' drop some files here, or click to select files</p>}
              </div>
            )}
          </Dropzone>
        )}
      </div>
      {displayURL && <img src={displayURL} alt="Location" />}
    </div>
  );
};
